import { useState } from 'react';
import { read, utils } from 'xlsx';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface ExcelRow {
  stockCode: string;
  stockName: string;
  quantity: number;
  totalValue: number;
  localCurrency: string;
  gainPct: number;
  addedValueChf: number;
  pricePerShare: number;
  initialInvestment: number;
}

export function ExcelImport({ onImportComplete }: { onImportComplete: () => void }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [minLine, setMinLine] = useState<number>(2);
  const [maxLine, setMaxLine] = useState<number>(100);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { toast } = useToast();

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      toast({
        title: "No file selected",
        description: "Please select an Excel file first",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];

      // Helper to safely read a cell value
      const getCell = (addr: string) => worksheet?.[addr]?.v;

      const positions: ExcelRow[] = [];
      for (let r = minLine; r <= maxLine; r++) {
        const stockCode = String(getCell(`CO${r}`) || '').trim();
        const stockName = String(getCell(`C${r}`) || '').trim();
        const quantity = Number(getCell(`G${r}`) || 0);
        
        if (!stockCode || quantity <= 0) continue;

        positions.push({
          stockCode,
          stockName,
          quantity,
          totalValue: Number(getCell(`J${r}`) || 0),
          localCurrency: String(getCell(`K${r}`) || 'CHF').trim(),
          gainPct: Number(getCell(`BR${r}`) || 0),
          addedValueChf: Number(getCell(`BQ${r}`) || 0),
          pricePerShare: Number(getCell(`BU${r}`) || 0),
          initialInvestment: Number(getCell(`BN${r}`) || 0),
        });
      }


      if (positions.length === 0) {
        throw new Error('No valid positions found in the specified line range');
      }

      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Delete existing positions to avoid duplicates
      await supabase
        .from('positions')
        .delete()
        .eq('user_id', user.id);

      // Calculate buy price from total value and quantity
      const { error } = await supabase
        .from('positions')
        .insert(
          positions.map(p => ({
            ticker: p.stockCode,
            stock_name: p.stockName,
            buy_price: p.totalValue / p.quantity,
            quantity: p.quantity,
            total_value: p.totalValue,
            local_currency: p.localCurrency,
            gain_pct: p.gainPct,
            added_value_chf: p.addedValueChf,
            price_per_share: p.pricePerShare,
            initial_investment: p.initialInvestment,
            user_id: user.id
          }))
        );

      if (error) throw error;

      toast({
        title: "Import Successful",
        description: `Imported ${positions.length} position${positions.length !== 1 ? 's' : ''}. Generating report...`,
      });

      // Send portfolio report email
      supabase.functions.invoke('send-portfolio-report', {
        body: {
          positions: positions.map(p => ({
            ticker: p.stockCode,
            stock_name: p.stockName,
            quantity: p.quantity,
            buy_price: p.totalValue / p.quantity,
            price_per_share: p.pricePerShare,
            total_value: p.totalValue,
            gain_pct: p.gainPct,
            added_value_chf: p.addedValueChf,
            local_currency: p.localCurrency
          })),
          userEmail: user.email,
          importType: 'Excel'
        }
      }).then(response => {
        if (response.error) {
          console.error('Failed to send portfolio report:', response.error);
        } else {
          console.log('Portfolio report sent successfully');
          toast({
            title: "Report Sent",
            description: "Portfolio analysis report has been sent to your email",
          });
        }
      });

      // Send contributors report via email
      const contributors = positions
        .filter(p => p.addedValueChf > 0)
        .map(p => ({
          ticker: p.stockCode,
          addedValueChf: p.addedValueChf,
          gainPct: p.gainPct,
          totalValue: p.totalValue,
          localCurrency: p.localCurrency
        }));

      supabase.functions.invoke('send-contributors-report', {
        body: { 
          contributors,
          userEmail: user.email
        }
      }).then(response => {
        if (response.error) {
          console.error('Failed to send contributors report:', response.error);
        } else {
          console.log('Contributors report sent successfully');
        }
      });

      // Schedule alert email to be sent in 4 minutes
      supabase.functions.invoke('send-portfolio-alerts', {
        body: { 
          userId: user.id,
          delayMinutes: 4 
        }
      }).then(response => {
        if (response.error) {
          console.error('Failed to schedule alert email:', response.error);
        } else {
          console.log('Alert email scheduled successfully');
        }
      });

      setOpen(false);
      setSelectedFile(null);
      onImportComplete();
    } catch (error: any) {
      toast({
        title: "Import Failed",
        description: error.message || "Failed to import Excel file",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="lg">
          <Upload className="mr-2 h-5 w-5" />
          Import Excel
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Import Positions from Excel</DialogTitle>
          <DialogDescription>
            Configure line range and upload your Excel file
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="min-line">Start Line</Label>
              <Input
                id="min-line"
                type="number"
                min="1"
                value={minLine}
                onChange={(e) => setMinLine(Number(e.target.value))}
                disabled={loading}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="max-line">End Line</Label>
              <Input
                id="max-line"
                type="number"
                min="1"
                value={maxLine}
                onChange={(e) => setMaxLine(Number(e.target.value))}
                disabled={loading}
              />
            </div>
          </div>
          
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <label htmlFor="excel-upload" className="cursor-pointer">
              <span className="text-sm text-muted-foreground">
                {selectedFile ? selectedFile.name : 'Click to select Excel file'}
              </span>
              <input
                id="excel-upload"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleFileSelect}
                disabled={loading}
              />
            </label>
          </div>

          <Button 
            onClick={handleImport} 
            disabled={loading || !selectedFile}
            className="w-full"
          >
            {loading ? 'Importing...' : 'Import'}
          </Button>
          
          <div className="text-xs text-muted-foreground">
            <p className="font-semibold mb-1">Expected columns:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>Column C: Stock full name</li>
              <li>Column CO: Stock code</li>
              <li>Column G: Quantity</li>
              <li>Column J: Total value</li>
              <li>Column K: Local currency</li>
              <li>Column BN: Initial investment</li>
              <li>Column BR: Gain %</li>
              <li>Column BQ: Added value (CHF)</li>
              <li>Column BU: Price per share</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}